/* eslint-disable no-console */
import { invoke } from '@tauri-apps/api/core'

import { Result } from '@blade/std-lib'
import { LOG_TAURI_INVOKE_ENABLED } from '@root/shared/environment'

import { SecureStorageError } from '../secure-storage-error'

import type { InvokeArgs } from '@tauri-apps/api/core'

type TauriCommand =
  | 'import_wallet'
  | 'export_wallet'
  | 'remove_wallet'
  | 'create_wallet'
  | 'create_mnemonic'
  | 'unlock'
  | 'initialize'
  | 'lock'
  | 'get_wallets'
  | 'update_wallets'
  | 'verify_password'
  | 'change_password'
  | 'sign_transaction'
  | 'sign_transactions'
  | 'sign_permit'
  | 'sign_plain_transaction'
  | 'sign_and_send_plain_transaction'
  | 'build_and_sign_swap_transaction'
  | 'build_sign_and_send_swap_transaction'
  | 'bulk_build_sign_and_send_swap_transaction'
  | 'session'
  | 'has_wallets'
  | 'find_closest_sender_location'
  | 'warm_sender_connection'
  | 'sign_and_send_pathfinder_tx'
  | 'get_api_settings'
  | 'set_api_settings'
  | 'clear_api_settings'

// ---------------------------------------------------------------------------
// Browser mode: HTTP transport to the local Tauri server
// ---------------------------------------------------------------------------

const isTauriWebview = (): boolean => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return !!(window as any).__TAURI_INTERNALS__
  } catch {
    return false
  }
}

const BROWSER_API_PORT = 12_582

const httpInvoke = async <T>(
  fnName: string,
  args?: Record<string, unknown>,
): Promise<T> => {
  const resp = await fetch(
    `http://deluge.localhost:${BROWSER_API_PORT}/api/invoke/${fnName}`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(args ?? {}),
    },
  )
  if (!resp.ok) {
    const text = await resp.text()
    throw text
  }
  return resp.json() as Promise<T>
}

// ---------------------------------------------------------------------------
// Unified invoke — picks transport based on runtime environment
// ---------------------------------------------------------------------------

export const safeInvoke = async <T>(
  fnName: TauriCommand,
  args?: InvokeArgs,
): Promise<Result.Result<T, SecureStorageError>> => {
  try {
    if (LOG_TAURI_INVOKE_ENABLED) {
      console.log('[TAURI INVOKE]', fnName, args)
    }

    const ipcStart = performance.now()
    const result = isTauriWebview()
      ? await invoke<T>(fnName, args)
      : await httpInvoke<T>(fnName, args as Record<string, unknown>)
    const ipcMs = performance.now() - ipcStart

    if (LOG_TAURI_INVOKE_ENABLED) {
      console.log(
        `[TAURI INVOKE RESULT] ${fnName} (${ipcMs.toFixed(1)}ms)`,
        result,
      )
    }
    return Result.succeed(result)
  } catch (error) {
    if (LOG_TAURI_INVOKE_ENABLED) {
      console.log('[TAURI INVOKE ERROR]', fnName, args, error)
    }

    return Result.fail(
      new SecureStorageError({
        cause: error,
        method: `tauri-secure-storage/${fnName}`,
      }),
    )
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
;(globalThis as any).__TAURI_INVOKE = safeInvoke
